/**
 * 2048 Engine — Core Game Logic
 *
 * Handles board initialization, tile movement, merging, scoring,
 * win/loss detection, and undo.
 *
 * Tile Movement Algorithm (per direction):
 * 1. Extract the row/column in the direction of movement
 * 2. Remove empty cells (compact)
 * 3. Merge adjacent equal tiles (only once per move)
 * 4. Fill remaining with empty cells
 * 5. Track if anything moved (if not, don't add new tile)
 */

import {
  Board2048,
  Tile,
  Direction,
  Game2048State,
  Game2048Snapshot,
  DEFAULT_BOARD_SIZE,
  WIN_TILE_VALUE,
} from './types';

export class Game2048Engine {
  private gameState: Game2048State | null = null;
  private nextTileId = 1;

  // =========================================================================
  // Public API
  // =========================================================================

  /** Initialize a new game with an empty board and 2 random tiles. */
  initializeGame(boardSize: number = DEFAULT_BOARD_SIZE): Game2048State {
    this.nextTileId = 1;

    const board = this.createEmptyBoard(boardSize);

    this.gameState = {
      board,
      score: 0,
      bestScore: this.loadBestScore(),
      gameStatus: 'playing',
      keepPlaying: false,
      moveHistory: [],
      tiles: [],
      boardSize,
      gameId: this.generateId(),
      startedAt: Date.now(),
      moveCount: 0,
    };

    // Add 2 random tiles
    this.addRandomTile();
    this.addRandomTile();

    // Update tiles list
    this.updateTilesList();

    return this.getGameState();
  }

  /** Get a deep copy of the current game state. */
  getGameState(): Game2048State {
    if (!this.gameState) {
      throw new Error('Game not initialized');
    }
    return JSON.parse(JSON.stringify(this.gameState));
  }

  /** Execute a move in the given direction. Returns updated state. */
  move(direction: Direction): Game2048State {
    if (!this.gameState) throw new Error('Game not initialized');
    if (this.gameState.gameStatus === 'game-over') throw new Error('Game is over');
    if (this.gameState.gameStatus === 'won' && !this.gameState.keepPlaying) {
      throw new Error('Game is won. Choose to keep playing first.');
    }

    // Save snapshot for undo (before the move)
    const snapshot = this.createSnapshot();

    // Clear merge/new flags
    this.clearTileFlags();

    // Perform the move
    const moved = this.executeMove(direction);

    if (!moved) {
      // Nothing moved — don't add tile or record move
      return this.getGameState();
    }

    // Add a new random tile
    this.addRandomTile();

    // Update move count
    this.gameState.moveCount++;

    // Save snapshot to history
    this.gameState.moveHistory.push(snapshot);
    // Limit history to 20 moves
    if (this.gameState.moveHistory.length > 20) {
      this.gameState.moveHistory.shift();
    }

    // Update best score
    if (this.gameState.score > this.gameState.bestScore) {
      this.gameState.bestScore = this.gameState.score;
      this.saveBestScore(this.gameState.bestScore);
    }

    // Check win condition
    if (!this.gameState.keepPlaying && this.hasWon()) {
      this.gameState.gameStatus = 'won';
    }

    // Check game over
    if (!this.canMove()) {
      this.gameState.gameStatus = 'game-over';
    }

    // Update tiles list
    this.updateTilesList();

    return this.getGameState();
  }

  /** Continue playing after winning. */
  keepPlaying(): Game2048State {
    if (!this.gameState) throw new Error('Game not initialized');
    if (this.gameState.gameStatus !== 'won') throw new Error('Game is not in won state');

    this.gameState.keepPlaying = true;
    this.gameState.gameStatus = 'playing';

    return this.getGameState();
  }

  /** Undo the last move. */
  undo(): Game2048State {
    if (!this.gameState) throw new Error('Game not initialized');
    if (this.gameState.moveHistory.length === 0) return this.getGameState();

    const snapshot = this.gameState.moveHistory.pop()!;
    this.gameState.board = snapshot.board;
    this.gameState.score = snapshot.score;
    this.gameState.tiles = snapshot.tiles;
    this.gameState.gameStatus = snapshot.gameStatus === 'game-over' ? 'playing' : snapshot.gameStatus;
    this.gameState.moveCount = Math.max(0, this.gameState.moveCount - 1);

    // Rebuild nextTileId from existing tiles
    this.rebuildNextTileId();

    // Update tiles list
    this.updateTilesList();

    return this.getGameState();
  }

  /** Check if any moves are possible. */
  canMove(): boolean {
    if (!this.gameState) return false;
    const { board, boardSize } = this.gameState;

    // Check for empty cells
    for (let r = 0; r < boardSize; r++) {
      for (let c = 0; c < boardSize; c++) {
        if (!board[r][c]) return true;
      }
    }

    // Check for adjacent matching tiles
    for (let r = 0; r < boardSize; r++) {
      for (let c = 0; c < boardSize; c++) {
        const tile = board[r][c];
        if (!tile) continue;

        // Check right
        if (c + 1 < boardSize && board[r][c + 1]?.value === tile.value) return true;
        // Check down
        if (r + 1 < boardSize && board[r + 1][c]?.value === tile.value) return true;
      }
    }

    return false;
  }

  /** Check if the game is over (no moves possible). */
  isGameOver(): boolean {
    return !this.canMove();
  }

  /** Check if any tile has reached 2048. */
  hasWon(): boolean {
    if (!this.gameState) return false;
    const { board, boardSize } = this.gameState;

    for (let r = 0; r < boardSize; r++) {
      for (let c = 0; c < boardSize; c++) {
        if (board[r][c] && board[r][c]!.value >= WIN_TILE_VALUE) return true;
      }
    }
    return false;
  }

  /** Get the current score. */
  getScore(): number {
    return this.gameState?.score ?? 0;
  }

  /** Get the best score. */
  getBestScore(): number {
    return this.gameState?.bestScore ?? this.loadBestScore();
  }

  /** Get the highest tile value on the board. */
  getHighestTile(): number {
    if (!this.gameState) return 0;
    const { board, boardSize } = this.gameState;
    let max = 0;

    for (let r = 0; r < boardSize; r++) {
      for (let c = 0; c < boardSize; c++) {
        if (board[r][c] && board[r][c]!.value > max) {
          max = board[r][c]!.value;
        }
      }
    }
    return max;
  }

  /** Serialize game state for persistence. */
  serialize(): string {
    return JSON.stringify(this.gameState);
  }

  /** Deserialize and restore game state. */
  deserialize(data: string): Game2048State {
    this.gameState = JSON.parse(data);
    this.rebuildNextTileId();
    return this.getGameState();
  }

  // =========================================================================
  // Move Execution
  // =========================================================================

  private executeMove(direction: Direction): boolean {
    if (!this.gameState) return false;
    const { board, boardSize } = this.gameState;
    let moved = false;

    const processLine = (line: (Tile | null)[]): { newLine: (Tile | null)[]; scoreGained: number; didMove: boolean } => {
      // Step 1: Compact — remove nulls
      const compacted = line.filter((t): t is Tile => t !== null);
      let scoreGained = 0;
      const merged: Tile[] = [];

      // Step 2: Merge adjacent equal tiles
      let i = 0;
      while (i < compacted.length) {
        if (i + 1 < compacted.length && compacted[i].value === compacted[i + 1].value) {
          const newValue = compacted[i].value * 2;
          const mergedTile: Tile = {
            id: this.nextTileId++,
            value: newValue,
            row: 0, // Will be set later
            col: 0, // Will be set later
            mergedFrom: true,
          };
          merged.push(mergedTile);
          scoreGained += newValue;
          i += 2; // Skip both merged tiles
        } else {
          // Keep the tile, update its previous position
          const tile = compacted[i];
          merged.push({
            ...tile,
            previousRow: tile.row,
            previousCol: tile.col,
            mergedFrom: false,
            isNew: false,
          });
          i++;
        }
      }

      // Step 3: Fill remaining with nulls
      const newLine: (Tile | null)[] = [...merged];
      while (newLine.length < boardSize) {
        newLine.push(null);
      }

      // Check if anything moved
      let didMove = false;
      for (let j = 0; j < boardSize; j++) {
        const oldTile = line[j];
        const newTile = newLine[j];
        if ((oldTile === null) !== (newTile === null)) {
          didMove = true;
          break;
        }
        if (oldTile && newTile && oldTile.value !== newTile.value) {
          didMove = true;
          break;
        }
      }

      return { newLine, scoreGained, didMove };
    };

    if (direction === 'left') {
      for (let r = 0; r < boardSize; r++) {
        const line = board[r].slice();
        const { newLine, scoreGained, didMove } = processLine(line);
        if (didMove) moved = true;
        this.gameState.score += scoreGained;
        for (let c = 0; c < boardSize; c++) {
          if (newLine[c]) {
            newLine[c]!.row = r;
            newLine[c]!.col = c;
          }
          board[r][c] = newLine[c];
        }
      }
    } else if (direction === 'right') {
      for (let r = 0; r < boardSize; r++) {
        const line = board[r].slice().reverse();
        const { newLine, scoreGained, didMove } = processLine(line);
        if (didMove) moved = true;
        this.gameState.score += scoreGained;
        const reversed = newLine.reverse();
        for (let c = 0; c < boardSize; c++) {
          if (reversed[c]) {
            reversed[c]!.row = r;
            reversed[c]!.col = c;
          }
          board[r][c] = reversed[c];
        }
      }
    } else if (direction === 'up') {
      for (let c = 0; c < boardSize; c++) {
        const line: (Tile | null)[] = [];
        for (let r = 0; r < boardSize; r++) {
          line.push(board[r][c]);
        }
        const { newLine, scoreGained, didMove } = processLine(line);
        if (didMove) moved = true;
        this.gameState.score += scoreGained;
        for (let r = 0; r < boardSize; r++) {
          if (newLine[r]) {
            newLine[r]!.row = r;
            newLine[r]!.col = c;
          }
          board[r][c] = newLine[r];
        }
      }
    } else if (direction === 'down') {
      for (let c = 0; c < boardSize; c++) {
        const line: (Tile | null)[] = [];
        for (let r = boardSize - 1; r >= 0; r--) {
          line.push(board[r][c]);
        }
        const { newLine, scoreGained, didMove } = processLine(line);
        if (didMove) moved = true;
        this.gameState.score += scoreGained;
        const reversed = newLine.reverse();
        for (let r = 0; r < boardSize; r++) {
          if (reversed[r]) {
            reversed[r]!.row = r;
            reversed[r]!.col = c;
          }
          board[r][c] = reversed[r];
        }
      }
    }

    return moved;
  }

  // =========================================================================
  // Board Helpers
  // =========================================================================

  private createEmptyBoard(size: number): Board2048 {
    return Array.from({ length: size }, () =>
      Array.from({ length: size }, () => null)
    );
  }

  private addRandomTile(): void {
    if (!this.gameState) return;
    const { board, boardSize } = this.gameState;

    const emptyCells: { row: number; col: number }[] = [];
    for (let r = 0; r < boardSize; r++) {
      for (let c = 0; c < boardSize; c++) {
        if (!board[r][c]) {
          emptyCells.push({ row: r, col: c });
        }
      }
    }

    if (emptyCells.length === 0) return;

    const { row, col } = emptyCells[Math.floor(Math.random() * emptyCells.length)];
    const value = Math.random() < 0.9 ? 2 : 4;

    board[row][col] = {
      id: this.nextTileId++,
      value,
      row,
      col,
      isNew: true,
    };
  }

  private clearTileFlags(): void {
    if (!this.gameState) return;
    const { board, boardSize } = this.gameState;

    for (let r = 0; r < boardSize; r++) {
      for (let c = 0; c < boardSize; c++) {
        const tile = board[r][c];
        if (tile) {
          tile.mergedFrom = false;
          tile.isNew = false;
          tile.previousRow = undefined;
          tile.previousCol = undefined;
        }
      }
    }
  }

  private updateTilesList(): void {
    if (!this.gameState) return;
    const { board, boardSize } = this.gameState;
    const tiles: Tile[] = [];

    for (let r = 0; r < boardSize; r++) {
      for (let c = 0; c < boardSize; c++) {
        if (board[r][c]) {
          tiles.push({ ...board[r][c]! });
        }
      }
    }

    this.gameState.tiles = tiles;
  }

  private createSnapshot(): Game2048Snapshot {
    if (!this.gameState) throw new Error('Game not initialized');
    return {
      board: JSON.parse(JSON.stringify(this.gameState.board)),
      score: this.gameState.score,
      tiles: JSON.parse(JSON.stringify(this.gameState.tiles)),
      gameStatus: this.gameState.gameStatus,
    };
  }

  private rebuildNextTileId(): void {
    if (!this.gameState) return;
    let maxId = 0;
    const { board, boardSize } = this.gameState;

    for (let r = 0; r < boardSize; r++) {
      for (let c = 0; c < boardSize; c++) {
        if (board[r][c] && board[r][c]!.id > maxId) {
          maxId = board[r][c]!.id;
        }
      }
    }
    this.nextTileId = maxId + 1;
  }

  // =========================================================================
  // Best Score Persistence
  // =========================================================================

  private loadBestScore(): number {
    try {
      const saved = localStorage.getItem('game_2048_best_score');
      return saved ? parseInt(saved, 10) : 0;
    } catch {
      return 0;
    }
  }

  private saveBestScore(score: number): void {
    try {
      localStorage.setItem('game_2048_best_score', String(score));
    } catch {
      // Silently fail
    }
  }

  // =========================================================================
  // Utility
  // =========================================================================

  private generateId(): string {
    return `2048_${Date.now()}_${Math.random().toString(36).substring(2, 8)}`;
  }
}
